import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { sendGroupGoalAchievedNotifications } from '../../../hooks/useNotifications';

interface MemberTrendPoint {
  weekLabel: string;
  weekStart: Date;
  [userId: string]: number | Date | string;
}

interface MemberInfo {
  user_id: string;
  username: string;
  avatar_url?: string;
  color: string;
}

interface MemberWeeklyTrendChartProps {
  groupId: string;
  groupName?: string;
  memberUserIds: string[];
}

const PALETTE = [
  '#10b981',
  '#06b6d4',
  '#f59e0b',
  '#f43f5e',
  '#8b5cf6',
  '#84cc16',
  '#fb923c',
  '#e879f9',
  '#22d3ee',
  '#4ade80',
];

const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekLabel = (date: Date): string => {
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${m}/${d}〜`;
};

const getLastNWeekStarts = (n: number): Date[] => {
  const weeks: Date[] = [];
  const now = new Date();
  const currentWeekStart = getWeekStart(now);
  for (let i = n - 1; i >= 0; i--) {
    const ws = new Date(currentWeekStart);
    ws.setDate(ws.getDate() - i * 7);
    weeks.push(ws);
  }
  return weeks;
};

export default function MemberWeeklyTrendChart({
  groupId,
  groupName = 'グループ',
  memberUserIds,
}: MemberWeeklyTrendChartProps) {
  const { user } = useAuth();
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [chartData, setChartData] = useState<MemberTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [hiddenMembers, setHiddenMembers] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    weekLabel: string;
    data: { member: MemberInfo; hours: number }[];
  } | null>(null);
  const [goalHours, setGoalHours] = useState<number | null>(null);
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgWidth, setSvgWidth] = useState(600);

  const WEEKS = 6;
  const CHART_H = 180;
  const PAD_LEFT = 36;
  const PAD_RIGHT = 12;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 36;

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSvgWidth(Math.max(entry.contentRect.width, 280));
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const fetchGoal = useCallback(async () => {
    const { data } = await supabase
      .from('group_weekly_goals')
      .select('goal_hours')
      .eq('group_id', groupId)
      .maybeSingle();
    if (data) {
      setGoalHours(Number(data.goal_hours));
      setGoalInput(String(data.goal_hours));
    }
  }, [groupId]);

  const fetchData = useCallback(async () => {
    if (memberUserIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const weekStarts = getLastNWeekStarts(WEEKS);
      const oldest = weekStarts[0];
      const cutoff = new Date(oldest);
      cutoff.setDate(cutoff.getDate() - 1);

      const [{ data: entries }, { data: profiles }] = await Promise.all([
        supabase
          .from('diary_entries')
          .select('user_id, study_hours, created_at')
          .in('user_id', memberUserIds)
          .gte('created_at', cutoff.toISOString())
          .not('study_hours', 'is', null),
        supabase
          .from('user_profiles')
          .select('user_id, username, avatar_url')
          .in('user_id', memberUserIds),
      ]);

      const memberInfos: MemberInfo[] = memberUserIds.map((uid, idx) => {
        const profile = profiles?.find(p => p.user_id === uid);
        return {
          user_id: uid,
          username: profile?.username || '不明',
          avatar_url: profile?.avatar_url,
          color: PALETTE[idx % PALETTE.length],
        };
      });
      setMembers(memberInfos);

      const weekMap: Record<string, Record<string, number>> = {};
      weekStarts.forEach(ws => {
        weekMap[ws.toISOString()] = {};
        memberUserIds.forEach(uid => { weekMap[ws.toISOString()][uid] = 0; });
      });

      (entries || []).forEach(entry => {
        const entryDate = new Date(entry.created_at);
        const ws = getWeekStart(entryDate);
        const key = ws.toISOString();
        if (weekMap[key] !== undefined && entry.user_id) {
          weekMap[key][entry.user_id] = (weekMap[key][entry.user_id] || 0) + (Number(entry.study_hours) || 0);
        }
      });

      const points: MemberTrendPoint[] = weekStarts.map(ws => {
        const point: MemberTrendPoint = {
          weekLabel: getWeekLabel(ws),
          weekStart: ws,
        };
        memberUserIds.forEach(uid => {
          point[uid] = weekMap[ws.toISOString()]?.[uid] || 0;
        });
        return point;
      });

      setChartData(points);

      // 目標達成チェック：今週の自分の学習時間が目標を超えていたら通知
      if (user && points.length > 0) {
        const { data: currentGoal } = await supabase
          .from('group_weekly_goals')
          .select('goal_hours')
          .eq('group_id', groupId)
          .maybeSingle();

        if (currentGoal && currentGoal.goal_hours) {
          const thisWeekPoint = points[points.length - 1];
          const myHoursThisWeek = (thisWeekPoint[user.id] as number) || 0;
          const goal = Number(currentGoal.goal_hours);

          if (myHoursThisWeek >= goal && goal > 0) {
            // 目標達成！全メンバーに通知
            await sendGroupGoalAchievedNotifications(
              groupId,
              groupName,
              memberUserIds,
              user.id,
              thisWeekPoint.weekLabel as string,
              myHoursThisWeek,
              goal
            );
          }
        }
      }
    } catch (err) {
      console.error('週間推移データ取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, [memberUserIds, groupId, user, groupName]);

  useEffect(() => {
    fetchData();
    fetchGoal();
  }, [fetchData, fetchGoal]);

  const handleSaveGoal = async () => {
    const val = parseFloat(goalInput);
    if (isNaN(val) || val < 0) return;
    setSavingGoal(true);
    try {
      const { data: existing } = await supabase
        .from('group_weekly_goals')
        .select('id')
        .eq('group_id', groupId)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('group_weekly_goals')
          .update({ goal_hours: val, updated_by: user?.id, updated_at: new Date().toISOString() })
          .eq('group_id', groupId);
      } else {
        await supabase
          .from('group_weekly_goals')
          .insert({ group_id: groupId, goal_hours: val, updated_by: user?.id });
      }
      setGoalHours(val);
      setShowGoalEditor(false);
    } catch (err) {
      console.error('目標時間保存エラー:', err);
    } finally {
      setSavingGoal(false);
    }
  };

  const toggleMember = (uid: string) => {
    setHiddenMembers(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const visibleMembers = members.filter(m => !hiddenMembers.has(m.user_id));
  const allHours = chartData.flatMap(p => visibleMembers.map(m => (p[m.user_id] as number) || 0));
  const dataMax = Math.max(...allHours, 1);
  const maxH = goalHours !== null ? Math.max(dataMax, goalHours * 1.1) : dataMax;
  const chartInnerW = svgWidth - PAD_LEFT - PAD_RIGHT;
  const chartInnerH = CHART_H - PAD_TOP - PAD_BOTTOM;
  const colW = chartInnerW / Math.max(chartData.length - 1, 1);

  const toX = (idx: number) => PAD_LEFT + idx * colW;
  const toY = (hours: number) => PAD_TOP + chartInnerH - (hours / maxH) * chartInnerH;

  const getPath = (uid: string): string => {
    if (chartData.length === 0) return '';
    const pts = chartData.map((p, i) => `${toX(i)},${toY((p[uid] as number) || 0)}`);
    return `M${pts.join(' L')}`;
  };

  const getAreaPath = (uid: string): string => {
    if (chartData.length === 0) return '';
    const baseY = PAD_TOP + chartInnerH;
    const pts = chartData.map((p, i) => `${toX(i)},${toY((p[uid] as number) || 0)}`);
    const firstX = toX(0);
    const lastX = toX(chartData.length - 1);
    return `M${firstX},${baseY} L${pts.join(' L')} L${lastX},${baseY} Z`;
  };

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || chartData.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const relX = mouseX - PAD_LEFT;
    let closestIdx = Math.round(relX / colW);
    closestIdx = Math.max(0, Math.min(chartData.length - 1, closestIdx));
    const point = chartData[closestIdx];
    const memberData = visibleMembers.map(m => ({
      member: m,
      hours: (point[m.user_id] as number) || 0,
    })).sort((a, b) => b.hours - a.hours);
    setTooltip({ x: toX(closestIdx), y: PAD_TOP, weekLabel: point.weekLabel, data: memberData });
  };

  const yTicks = [0, Math.round(maxH / 2), Math.round(maxH)];
  const goalY = goalHours !== null ? toY(goalHours) : null;

  return (
    <div className="mt-6 bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-white/10">
        <h3 className="text-sm sm:text-base font-bold text-white flex items-center gap-2">
          <i className="ri-line-chart-line text-teal-400 text-base sm:text-lg"></i>
          メンバー週間学習推移
          <span className="text-xs text-white/40 font-normal">（過去6週間）</span>
        </h3>
        <div className="flex items-center gap-2">
          {/* 目標時間設定ボタン */}
          <button
            onClick={() => { setShowGoalEditor(v => !v); setGoalInput(goalHours !== null ? String(goalHours) : ''); }}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              goalHours !== null
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30'
                : 'bg-white/10 text-white/50 border border-white/10 hover:bg-white/15 hover:text-white/70'
            }`}
            title="週間目標時間を設定"
          >
            <i className="ri-flag-line text-xs"></i>
            {goalHours !== null ? `目標: ${goalHours}h` : '目標設定'}
          </button>
          <button
            onClick={fetchData}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
            title="データを更新"
          >
            <i className="ri-refresh-line text-white/40 text-sm"></i>
          </button>
        </div>
      </div>

      {/* 目標時間エディター */}
      {showGoalEditor && (
        <div className="px-4 sm:px-6 py-3 bg-amber-500/10 border-b border-amber-500/20">
          <p className="text-xs text-amber-300/80 mb-2 flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            グループ全員の週間目標学習時間をグラフに点線で表示します
          </p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-[160px]">
              <input
                type="number"
                min="0"
                max="168"
                step="0.5"
                value={goalInput}
                onChange={e => setGoalInput(e.target.value)}
                placeholder="例: 20"
                className="w-full px-3 py-2 bg-white/10 border border-amber-500/30 text-white text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400/50 placeholder-white/30"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40">h/週</span>
            </div>
            <button
              onClick={handleSaveGoal}
              disabled={savingGoal}
              className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {savingGoal ? '保存中...' : '設定する'}
            </button>
            {goalHours !== null && (
              <button
                onClick={async () => {
                  await supabase.from('group_weekly_goals').delete().eq('group_id', groupId);
                  setGoalHours(null);
                  setGoalInput('');
                  setShowGoalEditor(false);
                }}
                className="px-3 py-2 bg-white/10 hover:bg-white/15 text-white/50 hover:text-white/70 text-xs rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                削除
              </button>
            )}
            <button
              onClick={() => setShowGoalEditor(false)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white/40 text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-8 h-8 rounded-full border-4 border-t-emerald-400 border-emerald-500/20 animate-spin"></div>
        </div>
      ) : chartData.length === 0 || visibleMembers.length === 0 ? (
        <div className="text-center py-10 text-white/40 text-sm">
          データがありません
        </div>
      ) : (
        <div className="p-3 sm:p-5">
          {/* Legend / toggle */}
          <div className="flex flex-wrap gap-2 mb-4">
            {members.map(m => {
              const hidden = hiddenMembers.has(m.user_id);
              return (
                <button
                  key={m.user_id}
                  onClick={() => toggleMember(m.user_id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    hidden
                      ? 'bg-white/5 border-white/10 text-white/30 line-through'
                      : 'border-white/20 text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  style={{ borderColor: hidden ? undefined : `${m.color}60` }}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: hidden ? '#ffffff30' : m.color }}
                  ></span>
                  {m.username}
                </button>
              );
            })}
            {/* 目標ラインの凡例 */}
            {goalHours !== null && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-amber-500/30 text-amber-300/80 bg-amber-500/10">
                <span className="w-5 h-0 border-t-2 border-dashed border-amber-400 flex-shrink-0"></span>
                目標 {goalHours}h
              </div>
            )}
          </div>

          {/* SVG Chart */}
          <div ref={containerRef} className="relative w-full">
            <svg
              ref={svgRef}
              width={svgWidth}
              height={CHART_H}
              className="overflow-visible"
              onMouseMove={handleSvgMouseMove}
              onMouseLeave={() => setTooltip(null)}
            >
              {/* Horizontal grid lines */}
              {yTicks.map((tick, i) => {
                const y = toY(tick);
                return (
                  <g key={i}>
                    <line
                      x1={PAD_LEFT}
                      y1={y}
                      x2={PAD_LEFT + chartInnerW}
                      y2={y}
                      stroke="rgba(255,255,255,0.08)"
                      strokeDasharray={i === 0 ? '0' : '3,3'}
                    />
                    <text
                      x={PAD_LEFT - 4}
                      y={y + 4}
                      fontSize={9}
                      fill="rgba(255,255,255,0.35)"
                      textAnchor="end"
                    >
                      {tick}h
                    </text>
                  </g>
                );
              })}

              {/* 目標時間ライン（水平点線） */}
              {goalY !== null && goalHours !== null && goalY >= PAD_TOP && goalY <= PAD_TOP + chartInnerH && (
                <g>
                  <line
                    x1={PAD_LEFT}
                    y1={goalY}
                    x2={PAD_LEFT + chartInnerW}
                    y2={goalY}
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                    strokeDasharray="6,4"
                    strokeOpacity={0.85}
                  />
                  {/* 目標ラベル */}
                  <rect
                    x={PAD_LEFT + chartInnerW - 52}
                    y={goalY - 11}
                    width={52}
                    height={14}
                    rx={4}
                    fill="rgba(245,158,11,0.2)"
                    stroke="rgba(245,158,11,0.4)"
                    strokeWidth={0.5}
                  />
                  <text
                    x={PAD_LEFT + chartInnerW - 26}
                    y={goalY + 1}
                    fontSize={9}
                    fill="#fbbf24"
                    textAnchor="middle"
                    fontWeight="600"
                  >
                    目標 {goalHours}h
                  </text>
                </g>
              )}

              {/* Vertical column lines */}
              {chartData.map((p, i) => (
                <g key={i}>
                  <line
                    x1={toX(i)}
                    y1={PAD_TOP}
                    x2={toX(i)}
                    y2={PAD_TOP + chartInnerH}
                    stroke="rgba(255,255,255,0.05)"
                  />
                  <text
                    x={toX(i)}
                    y={PAD_TOP + chartInnerH + 16}
                    fontSize={9}
                    fill="rgba(255,255,255,0.35)"
                    textAnchor="middle"
                  >
                    {p.weekLabel}
                  </text>
                </g>
              ))}

              {/* Area fills */}
              {visibleMembers.map(m => (
                <path
                  key={`area-${m.user_id}`}
                  d={getAreaPath(m.user_id)}
                  fill={m.color}
                  fillOpacity={0.07}
                />
              ))}

              {/* Lines */}
              {visibleMembers.map(m => (
                <path
                  key={`line-${m.user_id}`}
                  d={getPath(m.user_id)}
                  fill="none"
                  stroke={m.color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity={0.9}
                />
              ))}

              {/* Dots */}
              {visibleMembers.map(m =>
                chartData.map((p, i) => {
                  const h = (p[m.user_id] as number) || 0;
                  if (h === 0) return null;
                  return (
                    <circle
                      key={`dot-${m.user_id}-${i}`}
                      cx={toX(i)}
                      cy={toY(h)}
                      r={3}
                      fill={m.color}
                      stroke="rgba(10,10,10,0.6)"
                      strokeWidth={1}
                    />
                  );
                })
              )}

              {/* Tooltip vertical line */}
              {tooltip && (
                <line
                  x1={tooltip.x}
                  y1={PAD_TOP}
                  x2={tooltip.x}
                  y2={PAD_TOP + chartInnerH}
                  stroke="rgba(255,255,255,0.3)"
                  strokeDasharray="3,3"
                />
              )}
            </svg>

            {/* Tooltip floating box */}
            {tooltip && tooltip.data.length > 0 && (
              <div
                className="absolute pointer-events-none z-10 bg-gray-900 border border-white/20 rounded-xl shadow-2xl px-3 py-2.5 text-xs min-w-[130px]"
                style={{
                  left: tooltip.x + 10 > svgWidth - 140 ? tooltip.x - 150 : tooltip.x + 10,
                  top: 8,
                }}
              >
                <p className="text-white/50 font-medium mb-1.5 text-[10px] tracking-wide">
                  {tooltip.weekLabel}
                </p>
                {goalHours !== null && (
                  <div className="flex items-center gap-1.5 mb-1.5 pb-1.5 border-b border-white/10">
                    <span className="w-4 h-0 border-t-2 border-dashed border-amber-400 flex-shrink-0"></span>
                    <span className="text-amber-300/70 text-[10px]">目標</span>
                    <span className="text-amber-300 font-bold ml-auto tabular-nums">{goalHours}h</span>
                  </div>
                )}
                <div className="space-y-1">
                  {tooltip.data.map(({ member, hours }) => (
                    <div key={member.user_id} className="flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: member.color }}
                      ></span>
                      <span className="text-white/70 truncate max-w-[70px]">{member.username}</span>
                      <span className={`font-bold ml-auto tabular-nums ${goalHours !== null && hours >= goalHours ? 'text-emerald-400' : 'text-white'}`}>
                        {hours}h
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
